import React, { useMemo, useRef, useState } from "react";
import { ButtonGroup, Dropdown, Form, Modal, OverlayTrigger, Stack, Tooltip } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Row from 'react-bootstrap/Row';
import Select from 'react-select';
import { FieldErrors, UseFormReturn } from "react-hook-form";
import { defaultProductForm } from "./stolarz_form_defaults";
import { DBMaterialType } from "../database_types";
import { FormData, ProductData } from "./stolarz_form_types";
import { generatePreviewNoWatch } from './stolarz_form_email';
import { fromOptimikToFormData, jsonToOptimik3, removePLChars } from '../conversions/json_to_optimik';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { readOrderData, readProductData, writeFormData, writeOrderData } from "./StolarzFormCRUD";
import "./StolarzForm.css"

export let openHrefSkipConfirmation = false;
export type Option = { value: string; label: string; orderSaveName: string | undefined, product: ProductData }
export type GroupedOption = {
    readonly label: string;
    // readonly value: string | undefined;
    // readonly savedOrderName: string | undefined;
    readonly options: Option[]
}
export const defaultProductOption: Option = { value: "", label: "Domyślny zestaw", orderSaveName: undefined, product: defaultProductForm }

const defaultProductOptionGroup: GroupedOption = { label: "", options: [defaultProductOption] }

export function generateSelectProductOptions(input: string): GroupedOption[] {
    let savedOptions: GroupedOption[] = [defaultProductOptionGroup];
    const saved = readProductData();
    if (saved) {
        savedOptions.push({
            label: "Zapisane ręcznie",
            options: saved.map((product) => ({ value: product.productName, label: product.productName, orderSaveName: undefined, product: product }))
        });
    }
    const savedDataOrders = readOrderData();
    if (savedDataOrders) {
        const saved = savedDataOrders;
        for (const orderSaveName in saved) {
            const products: ProductData[] = saved[orderSaveName];
            const productOptions: Option[] = products.map((product) => ({ value: orderSaveName + product.productName, label: product.productName, orderSaveName: orderSaveName, product: product }))
            savedOptions.push({
                label: `Zamówienie: ${orderSaveName}`,
                options: productOptions,
            });
        }
    }
    return savedOptions;
}


function downloadOptimikCsv(values: FormData, errors: FieldErrors<FormData>) {
    let optimikCsv;
    if (Object.keys(errors).length === 0) {
        optimikCsv = jsonToOptimik3(values);
    } else {
        console.log("errors:", errors)
        const valuesButWithError = { ...values };
        valuesButWithError.summary = "Uwaga: Formularz zawiera bledy, patrz mail\n" + values.summary;
        optimikCsv = jsonToOptimik3(valuesButWithError);
    }
    const blob = new Blob([optimikCsv], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const date = new Date();
    const name = removePLChars(values.name).replace(/[^0-9A-Z]+/gi, "");
    link.download = `${name}-${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}_${date.getHours()}-${date.getMinutes()}.csv`;
    link.href = url;
    link.click();
}

export function SaveOrderModal(show: boolean, handleClose: () => void, mailPreview: React.JSX.Element, watch: any): React.JSX.Element {
    const date = new Date();
    const [orderSaveName, setOrderSaveName] = useState(
        `Zamówienie ${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()} ${date.getHours()}:${date.getMinutes()}`,
    );
    function handleSaveOrder() {
        let savedData = readOrderData()
        if (!savedData) {
            const toSave = { orderSaveName: watch("products") };
            writeOrderData(toSave);
            return;
        }
        let savedOrders = savedData;
        let orderSaveNameDeduplicate = orderSaveName;
        while (savedOrders[orderSaveNameDeduplicate] !== undefined) {
            orderSaveNameDeduplicate += " - copy";
        }
        savedOrders[orderSaveNameDeduplicate] = watch("products");
        writeOrderData(savedOrders);
        handleClose();
    }
    return <Modal show={show} onHide={handleClose} size="xl">
        <Modal.Header closeButton>
            <Modal.Title>Zapisz zamówienie na komputerze</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {mailPreview}
        </Modal.Body>
        <Form>
            <Form.Group controlId="OrderSaveNameForm">
                <Modal.Footer>
                    <Stack direction='horizontal' className='col-12 gap-2'>
                        <Form.Label>Nazwa</Form.Label>
                        <Form.Control
                            value={orderSaveName}
                            type="text"
                            onChange={e => setOrderSaveName(e.target.value)}
                            autoFocus />
                        <Button variant="secondary" onClick={handleClose}>
                            Anuluj
                        </Button>
                        <Button variant="primary" onClick={() => { handleSaveOrder() }}>
                            Zapisz
                        </Button>
                    </Stack>
                </Modal.Footer>
            </Form.Group>
        </Form>
    </Modal>;
}


export function LoadOrderModal(show: boolean, handleClose: () => void, setLoadedFormData: (val: FormData | undefined) => void, methods: UseFormReturn<FormData>, props: { materialsDB: DBMaterialType[]; edgesDB: DBMaterialType[]; },
    clearEdgeCodeCacheHooks: (() => void)[]
): React.JSX.Element {
    const [optimikCsvFilepath, setOptimikCsvFilepath] = useState<string>();
    const [optimikCsvText, setOptimikCsvText] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    function handleLoadFromText() {
        for (const hook of clearEdgeCodeCacheHooks) {
            hook();
        }
        const loadedFormData = fromOptimikToFormData(optimikCsvText);
        setLoadedFormData(loadedFormData);
        loadFormData(loadedFormData, methods)
        handleClose();
    }
    function handleLoadFromFile() {
        if (optimikCsvFilepath === undefined) {
            return
        }
        const files = inputRef.current?.files;
        if (!files) {
            return
        }
        const optimikFile = files[0]

        optimikFile.text().then((readString) => {
            for (const hook of clearEdgeCodeCacheHooks) {
                hook()
            }
            const loadedFormData = fromOptimikToFormData(readString);
            setLoadedFormData(loadedFormData);
            loadFormData(loadedFormData, methods)
        })
        handleClose();
    }

    return <Modal show={show} onHide={handleClose} size="xl">
        <Modal.Header closeButton>
            <Modal.Title> 🗁 Załaduj zapisane zamówienie </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Tabs defaultActiveKey="home"
                id="uncontrolled-tab-example"
                className="mb-3" >
                <Tab eventKey="home" title="Zapisane na komputerze">
                    {LoadOrderFromBrowserStorage(handleClose, setLoadedFormData, methods, props, clearEdgeCodeCacheHooks)}
                </Tab>
                <Tab eventKey="profile" title="Wczytaj z pliku.csv">
                    <Form>
                        <Stack direction='horizontal' className='col-12 gap-2'>
                            {/* <Form.Group controlId="OrderLoadFromFileForm"> */}
                            <Form.Control
                                ref={inputRef}
                                className='p-2 ms-auto'
                                onChange={e => setOptimikCsvFilepath(e.target.value)}
                                accept='csv'
                                type="file"
                            />
                            <Button variant="secondary" onClick={handleClose}>
                                Anuluj
                            </Button>
                            <Button variant="primary"
                                disabled={optimikCsvFilepath === undefined && optimikCsvFilepath !== ""}
                                onClick={() => { handleLoadFromFile() }}>
                                🗁 Załaduj (nadpisz obecne zamówienie)
                            </Button>
                            {/* </Form.Group> */}
                        </Stack>
                    </Form>
                </Tab>
                <Tab eventKey="contact" title="Wklej tekst" >
                    <Form>
                        <Form.Group controlId="OrderLoadFromStringForm">
                            <Stack direction='horizontal' className='col-12 gap-2'>
                                <div className='p-2 ms-auto'></div>
                                <Button variant="secondary" onClick={handleClose}>
                                    Anuluj
                                </Button>
                                <Button variant="primary" disabled={optimikCsvText === ""} onClick={() => { handleLoadFromText(); }}>
                                    🗁 Załaduj (nadpisz obecne zamówienie)
                                </Button>
                            </Stack>

                            <Form.Control as="textarea" rows={8}
                                value={optimikCsvText}
                                onChange={e => setOptimikCsvText(e.target.value)}
                                onBlur={() => { console.log(optimikCsvText) }}
                            />
                        </Form.Group>
                    </Form>
                </Tab>
            </Tabs>
        </Modal.Body>
        {/* <Modal.Footer>
        </Modal.Footer> */}
    </Modal>;

}

function LoadOrderFromBrowserStorage(handleClose: () => void, setLoadedFormData: (val: FormData | undefined) => void, methods: UseFormReturn<FormData>, props: { materialsDB: DBMaterialType[]; edgesDB: DBMaterialType[]; }, clearEdgeCodeCacheHooks: (() => void)[]) {
    const [orderLoadName, setOrderLoadName] = useState("");
    const [temporaryProductData, setTemporaryProductData] = useState<ProductData[]>();
    const [options, setOptions] = useState<{ value: string, label: string }[]>(() => {
        const saved = readOrderData();
        if (!saved) { return []; }
        return Object.keys(saved).map((val) => ({ value: val, label: val }));
    });
    const handleInputChange = (input: string) => {
        const saved = readOrderData();
        if (!saved) {
            console.log("Load order: no data ")
            setOptions([]);
            return;
        }
        const opts = Object.keys(saved).map((val) => ({ value: val, label: val }))
        if (input) {
            setOptions(() => opts.filter((opt) => opt && opt.value && opt.value.toLowerCase().search(input)))
        }
        setOptions(() => opts)
        setTemporaryProductData(saved[orderLoadName])
    };

    const mailPreview = useMemo(() => {
        if (temporaryProductData) {
            return generatePreviewNoWatch(temporaryProductData, props)
        }
    }, [props, temporaryProductData]) // todo how to memo form values

    function handleDeleteOrder() {
        let saved = readOrderData();
        if (!saved) { return }
        let savedOrders = saved;
        delete savedOrders[orderLoadName];
        writeOrderData(savedOrders);
        setTemporaryProductData(undefined);
        setOrderLoadName("");
    }

    return <Form>
        <Form.Group controlId="OrderLoadNameForm">
            <Stack direction='horizontal' className='col-12 gap-2'>
                <div style={{ minWidth: 300 }}>
                    <Select
                        classNamePrefix="lp-copy-sel"
                        placeholder="Wybierz zapisany produkt"
                        options={options}
                        onInputChange={handleInputChange}
                        onMenuOpen={() => handleInputChange("")}
                        onMenuClose={() => handleInputChange(orderLoadName)}
                        value={options.filter(function (option) { return option.value === orderLoadName; })}
                        onChange={val => {
                            const saved = readOrderData();
                            if (saved) {
                                if (val?.value) {
                                    setTemporaryProductData(saved[val?.value]);
                                }
                            }
                            setOrderLoadName(() => val?.value || "");
                        }} />
                </div>
                <div className="p-2 ms-auto"></div>
                <Button variant="danger" style={{ minWidth: 90 }} onClick={handleDeleteOrder}>
                    ✖ Usuń
                </Button>
                <Button variant="secondary" onClick={handleClose}>
                    Anuluj
                </Button>
                <Button disabled={temporaryProductData === undefined} variant="primary" onClick={() => {
                    loadFormProductData(temporaryProductData, methods);
                    const form: FormData = { ...methods.getValues() } // loadFormProductData updates form for us and we need to recreate the rest of form data
                    form.products = temporaryProductData || [];
                    setLoadedFormData(form);
                    for (const hook of clearEdgeCodeCacheHooks) {
                        hook()
                    }
                    setTemporaryProductData(() => undefined);
                    setOrderLoadName("");
                    handleClose();
                }}>
                    🗁 Załaduj (nadpisz obecne zamówienie)
                </Button>
            </Stack>
            {orderLoadName !== "" && <h1>Podgląd: "{orderLoadName}"</h1>}
            {/* {temporaryProductData !== undefined && <strong>{temporaryProductData[0].productName} {generatePreviewNoWatch(temporaryProductData, props)}</strong>} */}
            {orderLoadName !== "" ? mailPreview : "Wybierz zapisane zamówienie aby wygenerować podgląd"}
        </Form.Group>
    </Form>;
}

function loadFormData(data: FormData | undefined, methods: UseFormReturn<FormData>) {
    if (data === undefined) {
        console.error("no data to load");
        return;
    }
    methods.setValue("name", data.name);
    methods.setValue("email", data.email);
    methods.setValue("address", data.address);
    methods.setValue("postalCode", data.postalCode);
    methods.setValue("city", data.city);
    methods.setValue("nip", data.nip);
    methods.setValue("summary", data.summary);
    loadFormProductData(data.products, methods);
    methods.clearErrors();
}

function loadFormProductData(data: ProductData[] | undefined, methods: UseFormReturn<FormData>) {
    if (data === undefined) {
        console.error("no data to load");
        return;
    }

    methods.setValue("products", data, { shouldDirty: true, shouldTouch: true });
    for (let index = 0; index < data.length; index++) {
        const prod = data[index];
        // not sure if this setValue is necessary
        methods.setValue(`products.${index}.sheets`, prod.sheets, { shouldDirty: true, shouldTouch: false });
    }
    methods.clearErrors();
}

export function SummaryModal(show: boolean, handleClose: () => void, formData: FormData, methods: UseFormReturn<FormData>, props: { materialsDB: DBMaterialType[]; edgesDB: DBMaterialType[]; }, emailPreview: React.JSX.Element, htmlEmailPreview: string, hrefMailto: string): React.JSX.Element {
    const [showCopied, setShowCopied] = useState(false);
    const [showDownloaded, setShowDownloaded] = useState(false);
    let close = () => {
        handleClose(); setShowCopied(false); setShowDownloaded(false);
    }
    // todo calculate warnings
    return <Modal show={show} onHide={close} size="xl">
        <Modal.Header closeButton>
            <Modal.Title>Podsumowanie</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {emailPreview}
        </Modal.Body >
        <Modal.Footer>
            {showCopied && <p className="mr-auto me-auto">Skopiowano do schowka!</p> }
            {showDownloaded && <p className="mr-auto me-auto">Pobrano!</p> }
            <Button className='btn-block' variant='secondary' onClick={close}>
                Anuluj
            </Button>
            <Dropdown as={ButtonGroup}>
                <Button className='btn-block' onClick={() => {
                    writeFormData(formData);
                    downloadOptimikCsv(formData, methods.formState.errors);
                    setShowDownloaded(true);
                    // var parentOpener = window.opener;
                    // window.opener = null;
                    // window.open(hrefMailto, "");
                    // window.opener = parentOpener;
                    //                 handleShowSendMail();
                    //                 handleClose();
                }}>
                    🗎 Pobierz plik z zamówieniem
                </Button>
                <Dropdown.Toggle>
                    {/* <strong>{`${productIndex + 1}.`}</strong> */}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item>
                        <Row>
                            <Button className='btn-block' onClick={() => {
                                function listener(e: ClipboardEvent): any {
                                    e.clipboardData?.setData("text/html", htmlEmailPreview);
                                    e.clipboardData?.setData("text/plain", htmlEmailPreview); // todo provide plain text version
                                    e.preventDefault();
                                }
                                document.addEventListener("copy", listener);
                                document.execCommand("copy");
                                document.removeEventListener("copy", listener);
                                // e.clipboardData.setData("text/html", htmlEmailPreview);
                                // navigator.clipboard.writeText(htmlEmailPreview)
                                setShowCopied(true);
                            }}>
                                📋 Kopiuj podsumowanie do schowka
                            </Button>
                        </Row>
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>

            <p> i wyślij do <a href="mailto:biuro@stolarz.net.pl">biuro@stolarz.net.pl</a> </p>
        </Modal.Footer>
    </Modal >;
}
// export function SendEmailModal(show: boolean, handleShowSave: () => void, handleShowLoad: () => void, handleClose: () => void, formData: FormData, methods: UseFormReturn<FormData>, props: { materialsDB: DBMaterialType[]; edgesDB: DBMaterialType[]; }, htmlEmailPreview: string): React.JSX.Element {
//     return <Modal show={show} onHide={handleClose} size="xl">
//         <Modal.Header closeButton>
//             <Modal.Title>Zamówienie złożone!</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//             <p>
//                 Okno mailowe powinno się otworzyć automatycznie w twoim domyślnym programie pocztowym. Jeśli tak się nie stało skopiuj zawartość maila i wyślij go do <strong> <a href="mailto:biuro@stolarz.net.pl">biuro@stolarz.net.pl</a> </strong>
//                 i poczekaj na odpowiedź z wyceną.
//             </p>
//             <Row>
//                 <Button className='btn-block' onClick={() => {
//                     function listener(e: ClipboardEvent): any {
//                         e.clipboardData?.setData("text/html", htmlEmailPreview);
//                         e.clipboardData?.setData("text/plain", htmlEmailPreview); // todo provide plain text version
//                         e.preventDefault();
//                     }
//                     document.addEventListener("copy", listener);
//                     document.execCommand("copy");
//                     document.removeEventListener("copy", listener);
//                     // e.clipboardData.setData("text/html", htmlEmailPreview);
//                     // navigator.clipboard.writeText(htmlEmailPreview)
//                 }}>
//                     📋 1. Skopiuj przygotowanego maila z podsumowaniem
//                 </Button>
//             </Row>
//             <br></br>
//             <p>Do maila załącz plik produkcyjny z rozszerzeniem <strong>.csv</strong> - będzie nam potrzebny do wyceny</p>
//             <Row>
//                 <Button className='btn-block' onClick={() => downloadOptimikCsv(formData, methods.formState.errors)}>
//                     🗎 2. Pobierz plik wykonawczy{Object.keys(methods.formState.errors).length !== 0 ? ". Błędy w formularzu!" : ""}
//                 </Button>
//             </Row>
//         </Modal.Body >
//         <Modal.Footer>
//             <Stack direction='horizontal' className='gap-2'>
//                 <Button className='btn-block me-auto' variant='danger' onClick={() => window.location.href = "/"}>
//                     Nowe zamówienie
//                 </Button>
//                 <Button variant='secondary' onClick={() => {
//                     handleShowLoad();
//                     handleClose();
//                 }}>
//                     🗁 Załaduj zapisane zamówienie
//                 </Button>
//             </Stack>
//             <div className='me-auto'></div>
//             {/* <div className="p-2 ms-auto"></div> */}
//             <Button className='btn-block' variant='secondary' onClick={handleClose}>
//                 Zamknij
//             </Button>
//             <Button className='btn-block' onClick={() => {
//                 handleShowSave();
//                 handleClose();
//             }}>
//                 🖫 Zapisz obecne zamówienie
//             </Button>
//         </Modal.Footer>
//     </Modal >;
// }

